import { getDisplayName, getDisplayPhoto } from "../stores/userDisplayUtils";
import { formatRoomTime } from "./formatRoomTime";
import { getPreviewTextFromLastMessage } from "./messageMapping";

/** WS message → room list용 lastMessage 형태 (API와 동일: text, messageType, content, createdAt, attachments, camelCase) */
export function buildLastMessageFromWsMessage(message) {
  if (!message) return null;
  return {
    text: message.text || "",
    messageType: message.messageType || [],
    content: message.content
      ? { contentId: message.content.contentId, contentType: message.content.contentType }
      : undefined,
    createdAt: message.createdAt,
    attachments: Array.isArray(message.attachments) ? message.attachments : [],
  };
}

/** 앱 캐시(다른 서버에서 한번 갔다 온 값) → API member.name → userId 순으로 사용 */
const displayNameFromMember = (m) =>
  getDisplayName(m.userId) || m.name || m.userId || "";

/**
 * 단일 방의 표시 이름 계산 (나 / 1명 이름 / 이름 외 n명). 목록·WS 추가 시 동일 규칙 사용.
 * @param {Object} room - room (room.members: { userId, name? }[])
 * @param {string} myUserId - 현재 사용자 ID
 * @returns {Object} name이 설정된 새 room
 */
export function deriveRoomDisplayName(room, myUserId) {
  if (!room) return room;
  const members = room.members || [];
  const others = members.filter((m) => m.userId && m.userId !== myUserId);
  let name = room.name;
  let avatarUrl = room.avatarUrl ?? null;
  if (others.length === 0) {
    // members에 상대방이 없는 경우 createdBy로 폴백 (채팅방 수신 시 members가 비어있는 경우)
    const createdBy = room.createdBy;
    if (createdBy && createdBy !== myUserId) {
      name = getDisplayName(createdBy) || createdBy;
      avatarUrl = getDisplayPhoto(createdBy) ?? avatarUrl;
    } else {
      name = "나";
    }
  } else if (others.length === 1) {
    name = displayNameFromMember(others[0]);
    avatarUrl = getDisplayPhoto(others[0].userId) ?? avatarUrl;
  } else {
    name = `${displayNameFromMember(others[0])} 외 ${others.length - 1}명`;
    avatarUrl = getDisplayPhoto(others[0].userId) ?? avatarUrl;
  }
  return { ...room, name, avatarUrl };
}

/**
 * 방 목록 전체에 표시 이름 적용.
 * @param {Array} rooms - room 목록
 * @param {string} myUserId - 현재 사용자 ID
 * @returns {Array} name이 설정된 새 room 목록
 */
export function deriveRoomDisplayNames(rooms, myUserId) {
  if (!Array.isArray(rooms)) return [];
  return rooms.map((room) => deriveRoomDisplayName(room, myUserId));
}

/** MessageList용: room → { ...room, previewText, time, unreadCount } */
export function roomToListItem(room) {
  if (!room) return room;
  return {
    ...room,
    previewText: getPreviewTextFromLastMessage(room.lastMessage),
    time: formatRoomTime(room.lastMessage?.createdAt || room.updatedAt),
    unreadCount: room.unreadCount || 0,
  };
}

/** room[] → listItem[] (로드/WS 갱신 시 동일 변환 적용) */
export function roomListToListItems(rooms) {
  if (!Array.isArray(rooms)) return [];
  return rooms.map(roomToListItem);
}

/** ROOM.CREATED(WS) → room API와 동일한 형식(camelCase)으로 변환 */
export function normalizeRoomFromRoomCreated(room) {
  if (!room?.id) return null;
  const members = (room.members || []).map((m) => ({
    userId: m.userId || m.user_id,
    joinedAt: m.joinedAt || m.joined_at,
    leftAt: m.leftAt || m.left_at,
    lastReadSeq: m.lastReadSeq ?? m.last_read_seq,
    lastReadAt: m.lastReadAt || m.last_read_at,
  }));
  return {
    id: room.id,
    name: room.name,
    roomType: room.roomType || room.room_type,
    createdAt: room.createdAt || room.created_at,
    createdBy: room.createdBy || room.created_by,
    updatedAt: room.updatedAt || room.updated_at,
    lastMessageSeq: room.lastMessageSeq ?? room.last_message_seq ?? 0,
    lastMessage: null,
    unreadCount: 0,
    members,
  };
}

/** RECEIVED/ACK 메시지로 목록에 없는 방(나갔다가 상대가 보낸 경우)용 스텁 room 생성. myUserId로 표시 이름 계산 */
function buildStubRoomFromMessage(message, options = {}) {
  const roomId = message?.roomId;
  if (!roomId) return null;
  const senderId = message.senderId || message.sender_id;
  const members = senderId ? [{ userId: senderId }] : [];
  const lastMessage = buildLastMessageFromWsMessage(message);
  const stub = {
    id: roomId,
    name: "",
    roomType: "DIRECT",
    lastMessageSeq: message.messageSeq != null ? message.messageSeq : 0,
    lastMessage,
    updatedAt: message.createdAt,
    unreadCount: options.incrementUnread ? 1 : 0,
    members,
  };
  const myUserId = options.myUserId;
  return deriveRoomDisplayName(stub, myUserId);
}

/** ACK/RECEIVED 시 해당 방의 lastMessage·updatedAt 갱신 후 목록 맨 위로. 방이 없으면(나갔다가 받은 경우) 스텁으로 맨 위에 추가. incrementUnread 시 해당 방이 현재 열린 방이 아니면 unread +1 */
export function updateRoomListWithMessage(prevRooms, message, options = {}) {
  const roomId = message?.roomId;
  if (!roomId || !Array.isArray(prevRooms)) return prevRooms;
  const lastMessage = buildLastMessageFromWsMessage(message);
  const idx = prevRooms.findIndex((r) => r.id === roomId);
  if (idx < 0) {
    const stubRoom = buildStubRoomFromMessage(message, options);
    if (!stubRoom) return prevRooms;
    const newItem = roomToListItem(stubRoom);
    return [newItem, ...prevRooms];
  }
  const room = prevRooms[idx];
  const prevUnread = room.unreadCount || 0;
  const alreadyCounted =
    message.messageSeq != null &&
    room.lastMessageSeq != null &&
    message.messageSeq <= room.lastMessageSeq;
  const updated = {
    ...room,
    lastMessage,
    lastMessageSeq: message.messageSeq != null ? message.messageSeq : room.lastMessageSeq,
    updatedAt: message.createdAt || room.updatedAt,
    unreadCount: options.incrementUnread && !alreadyCounted ? prevUnread + 1 : prevUnread,
  };
  const rest = prevRooms.filter((_, i) => i !== idx);
  return [roomToListItem(updated), ...rest];
}
